/* ==========================================
   ARTICLE FAQ ACCORDION + DYNAMIC FAQ SCHEMA

   Supports:
   1. Separate question and answer elements
   2. Question + answer in same paragraph
   3. Bold questions followed by answers
   4. Multiple FAQ sections
   5. Dynamic FAQPage JSON-LD schema
========================================== */

document.addEventListener('DOMContentLoaded', function () {
  const article = document.querySelector('.article-faq-content');

  if (!article) return;

  const editor = article.querySelector('.rte') || article;

  /*
   * All successfully created FAQs will also be
   * stored here for JSON-LD FAQPage schema.
   */
  const faqSchemaItems = [];

  /* ==========================================
     HELPERS
  ========================================== */

  function getText(element) {
    return (element.textContent || '')
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function getHeadingLevel(element) {
    const match = element.tagName.match(/^H([1-6])$/);

    return match ? Number(match[1]) : null;
  }

  function isFaqHeading(element) {
    if (!/^H[1-6]$/.test(element.tagName)) {
      return false;
    }

    const text = getText(element)
      .toLowerCase()
      .replace(/[?:\-–—]/g, '')
      .trim();

    return (
      text === 'faq' ||
      text === 'faqs' ||
      text === 'frequently asked questions' ||
      text === 'frequently asked question'
    );
  }

  function cleanQuestionText(text) {
    return text
      .replace(
        /^(?:q(?:uestion)?\s*[\.\:\-\)]\s*)/i,
        ''
      )
      .replace(/\s+/g, ' ')
      .trim();
  }

  function cleanAnswerElement(element) {
    const clone = element.cloneNode(true);

    /*
     * Remove prefixes such as:
     *
     * A.
     * A:
     * Answer:
     */
    const walker = document.createTreeWalker(
      clone,
      NodeFilter.SHOW_TEXT
    );

    let node;

    while ((node = walker.nextNode())) {
      if (!node.textContent.trim()) {
        continue;
      }

      node.textContent = node.textContent.replace(
        /^\s*(?:a(?:nswer)?\s*[\.\:\-\)]\s*)/i,
        ''
      );

      break;
    }

    return clone;
  }

  /* ==========================================
     NORMALIZE SHOPIFY EDITOR HTML
  ========================================== */

  function isEditorWrapper(element) {
    if (element.tagName !== 'DIV') {
      return false;
    }

    if (
      element.classList.contains('article-faq-accordion') ||
      element.classList.contains('faq-item')
    ) {
      return false;
    }

    const children = Array.from(element.children);

    if (!children.length) {
      return false;
    }

    /*
     * Common editor / pasted-content wrappers.
     */
    if (
      element.classList.contains('OutlineElement') ||
      element.hasAttribute('data-mce-style') ||
      element.hasAttribute('data-mce-fragment')
    ) {
      return true;
    }

    /*
     * Simple DIV containing only one
     * paragraph or heading.
     */
    if (
      children.length === 1 &&
      /^(P|H1|H2|H3|H4|H5|H6)$/.test(
        children[0].tagName
      )
    ) {
      return true;
    }

    return false;
  }

  function unwrapElement(element) {
    const parent = element.parentNode;

    if (!parent) return;

    while (element.firstChild) {
      parent.insertBefore(
        element.firstChild,
        element
      );
    }

    element.remove();
  }

  /*
   * Work from inner wrappers to outer wrappers.
   */
  Array.from(editor.querySelectorAll('div'))
    .reverse()
    .forEach(function (div) {
      if (isEditorWrapper(div)) {
        unwrapElement(div);
      }
    });

  /* ==========================================
     QUESTION DETECTION
  ========================================== */

  function isQuestion(element, faqHeadingLevel) {
    const text = getText(element);

    if (!text) return false;

    const headingLevel =
      getHeadingLevel(element);

    /*
     * CASE 1:
     *
     * FAQ = H2
     * Question = H3
     *
     * or
     *
     * FAQ = H3
     * Question = H4
     */
    if (
      headingLevel &&
      headingLevel > faqHeadingLevel
    ) {
      return true;
    }

    /*
     * CASE 2:
     *
     * Q: What is...
     * Question: What is...
     */
    if (
      /^(?:q(?:uestion)?\s*[\.\:\-\)])/i.test(
        text
      )
    ) {
      return true;
    }

    /*
     * CASE 3:
     *
     * <p>
     *   <strong>Question?</strong>
     *   Answer...
     * </p>
     */
    if (element.tagName === 'P') {
      const bold = element.querySelector(
        ':scope > strong, :scope > b'
      );

      if (
        bold &&
        getText(bold).includes('?')
      ) {
        return true;
      }
    }

    /*
     * CASE 4:
     *
     * Question and answer may exist
     * in the same paragraph.
     */
    if (
      element.tagName === 'P' &&
      text.includes('?')
    ) {
      const questionPart =
        text.split('?')[0];

      if (
        questionPart.length <= 250 &&
        questionPart.split(/\s+/).length <= 35
      ) {
        return true;
      }
    }

    /*
     * CASE 5:
     *
     * Simple text DIV containing question.
     */
    if (
      element.tagName === 'DIV' &&
      element.children.length === 0 &&
      text.includes('?') &&
      text.indexOf('?') <= 250
    ) {
      return true;
    }

    return false;
  }

  /* ==========================================
     EXTRACT QUESTION + INLINE ANSWER
  ========================================== */

  function extractQuestionAndAnswer(element) {
    const fullText = getText(element);

    /*
     * First try:
     *
     * <p>
     *   <strong>Question?</strong>
     *   Answer text...
     * </p>
     */
    const bold = element.querySelector(
      ':scope > strong, :scope > b'
    );

    if (
      bold &&
      getText(bold).includes('?')
    ) {
      let firstMeaningfulNode =
        element.firstChild;

      while (
        firstMeaningfulNode &&
        firstMeaningfulNode.nodeType ===
          Node.TEXT_NODE &&
        !firstMeaningfulNode.textContent.trim()
      ) {
        firstMeaningfulNode =
          firstMeaningfulNode.nextSibling;
      }

      if (firstMeaningfulNode === bold) {
        const question =
          cleanQuestionText(
            getText(bold)
          );

        const answerElement =
          element.cloneNode(true);

        const clonedBold =
          answerElement.querySelector(
            ':scope > strong, :scope > b'
          );

        if (clonedBold) {
          clonedBold.remove();
        }

        const answerText =
          getText(answerElement);

        if (answerText) {
          return {
            question,
            answers: [
              cleanAnswerElement(answerElement)
            ]
          };
        }

        return {
          question,
          answers: []
        };
      }
    }

    /*
     * Second try:
     *
     * Question? Answer...
     *
     * in the same element.
     */
    const questionMark =
      fullText.indexOf('?');

    if (questionMark !== -1) {
      const question =
        cleanQuestionText(
          fullText.slice(
            0,
            questionMark + 1
          )
        );

      const answerText =
        fullText
          .slice(questionMark + 1)
          .trim();

      /*
       * Question and answer are separate.
       */
      if (!answerText) {
        return {
          question,
          answers: []
        };
      }

      /*
       * Preserve HTML formatting where possible.
       */
      const clone =
        element.cloneNode(true);

      const walker =
        document.createTreeWalker(
          clone,
          NodeFilter.SHOW_TEXT
        );

      let node;
      let foundQuestionMark = false;

      while ((node = walker.nextNode())) {
        if (foundQuestionMark) {
          break;
        }

        const markIndex =
          node.textContent.indexOf('?');

        if (markIndex !== -1) {
          node.textContent =
            node.textContent.slice(
              markIndex + 1
            );

          foundQuestionMark = true;
        } else {
          node.textContent = '';
        }
      }

      /*
       * If formatted extraction fails,
       * fall back to clean plain text.
       */
      if (
        !foundQuestionMark ||
        !getText(clone)
      ) {
        const paragraph =
          document.createElement('p');

        paragraph.textContent =
          answerText;

        return {
          question,
          answers: [paragraph]
        };
      }

      return {
        question,
        answers: [
          cleanAnswerElement(clone)
        ]
      };
    }

    /*
     * Heading question with answer
     * expected in following elements.
     */
    return {
      question:
        cleanQuestionText(fullText),
      answers: []
    };
  }

  /* ==========================================
     FIND FAQ SECTIONS
  ========================================== */

  const faqHeadings = Array.from(
    editor.querySelectorAll(
      'h1,h2,h3,h4,h5,h6'
    )
  ).filter(isFaqHeading);

  if (!faqHeadings.length) {
    return;
  }

  faqHeadings.forEach(function (
    faqHeading,
    sectionIndex
  ) {
    /*
     * Avoid processing same section twice.
     */
    if (
      faqHeading.dataset.faqProcessed ===
      'true'
    ) {
      return;
    }

    const faqHeadingLevel =
      getHeadingLevel(faqHeading) || 2;

    const faqGroups = [];

    let currentQuestion = null;
    let currentAnswers = [];
    let currentElements = [];

    /*
     * Save only valid question + answer pairs.
     */
    function saveCurrentGroup() {
      if (!currentQuestion) {
        return;
      }

      if (currentAnswers.length) {
        faqGroups.push({
          question: currentQuestion,
          answers: currentAnswers,
          originalElements:
            currentElements
        });
      }

      currentQuestion = null;
      currentAnswers = [];
      currentElements = [];
    }

    let current =
      faqHeading.nextElementSibling;

    /* ========================================
       READ EVERYTHING AFTER FAQ HEADING
    ======================================== */

    while (current) {
      const next =
        current.nextElementSibling;

      const currentHeadingLevel =
        getHeadingLevel(current);

      /*
       * Stop when next major section begins.
       *
       * Example:
       *
       * H2 FAQ
       *   H3 Question
       *   P Answer
       *
       * H2 Conclusion ← stop
       */
      if (
        currentHeadingLevel &&
        currentHeadingLevel <=
          faqHeadingLevel
      ) {
        break;
      }

      /*
       * Skip already-created accordion.
       */
      if (
        current.classList.contains(
          'article-faq-accordion'
        )
      ) {
        current = next;
        continue;
      }

      if (
        isQuestion(
          current,
          faqHeadingLevel
        )
      ) {
        /*
         * Save previous FAQ.
         */
        saveCurrentGroup();

        const extracted =
          extractQuestionAndAnswer(
            current
          );

        currentQuestion =
          extracted.question;

        currentAnswers =
          extracted.answers;

        currentElements = [current];
      } else if (currentQuestion) {
        /*
         * Everything after question,
         * until next question,
         * becomes answer content.
         */
        currentAnswers.push(
          cleanAnswerElement(current)
        );

        currentElements.push(current);
      }

      current = next;
    }

    /*
     * Save final FAQ.
     */
    saveCurrentGroup();

    /*
     * If no valid question + answer pairs,
     * leave original content untouched.
     */
    if (!faqGroups.length) {
      return;
    }

    /* ==========================================
       BUILD ACCORDION
    ========================================== */

    const accordion =
      document.createElement('div');

    accordion.className =
      'article-faq-accordion';

    accordion.setAttribute(
      'data-faq-section',
      String(sectionIndex + 1)
    );

    faqGroups.forEach(function (
      group,
      index
    ) {
      const questionText =
        cleanQuestionText(
          group.question
        );

      if (!questionText) {
        return;
      }

      const answerText =
        group.answers
          .map(function (element) {
            return getText(element);
          })
          .filter(Boolean)
          .join(' ')
          .trim();

      /*
       * Never create a broken FAQ.
       */
      if (!answerText) {
        return;
      }

      /* --------------------------
         FAQ ITEM
      -------------------------- */

      const item =
        document.createElement('div');

      item.className = 'faq-item';

      /* --------------------------
         QUESTION BUTTON
      -------------------------- */

      const questionButton =
        document.createElement('button');

      questionButton.type = 'button';

      questionButton.className =
        'faq-question';

      const answerId =
        `faq-answer-${sectionIndex}-${index}`;

      questionButton.setAttribute(
        'aria-expanded',
        'false'
      );

      questionButton.setAttribute(
        'aria-controls',
        answerId
      );

      const questionTextElement =
        document.createElement('span');

      questionTextElement.className =
        'faq-question-text';

      questionTextElement.textContent =
        questionText;

      /* --------------------------
         PLUS / MINUS ICON
      -------------------------- */

      const icon =
        document.createElement('span');

      icon.className = 'faq-icon';

      icon.setAttribute(
        'aria-hidden',
        'true'
      );

      questionButton.appendChild(
        questionTextElement
      );

      questionButton.appendChild(icon);

      /* --------------------------
         ANSWER
      -------------------------- */

      const answer =
        document.createElement('div');

      answer.className = 'faq-answer';

      answer.id = answerId;

      answer.hidden = true;

      group.answers.forEach(function (
        answerElement
      ) {
        answer.appendChild(
          answerElement.cloneNode(true)
        );
      });

      item.appendChild(
        questionButton
      );

      item.appendChild(answer);

      accordion.appendChild(item);

      /* ======================================
         ADD SAME FAQ TO JSON-LD COLLECTION
      ====================================== */

      faqSchemaItems.push({
        '@type': 'Question',
        name: questionText,
        acceptedAnswer: {
          '@type': 'Answer',
          text: answerText
        }
      });
    });

    /*
     * Only insert if actual FAQ items
     * were successfully created.
     */
    if (!accordion.children.length) {
      return;
    }

    /*
     * Insert accordion after FAQ heading.
     */
    faqHeading.insertAdjacentElement(
      'afterend',
      accordion
    );

    /*
     * Remove original FAQ content because
     * accordion now contains it.
     */
    faqGroups.forEach(function (group) {
      group.originalElements.forEach(
        function (element) {
          element.remove();
        }
      );
    });

    faqHeading.dataset.faqProcessed =
      'true';

    /* ==========================================
       ACCORDION INTERACTION
    ========================================== */

    accordion
      .querySelectorAll(
        '.faq-question'
      )
      .forEach(function (button) {
        button.addEventListener(
          'click',
          function () {
            const item =
              button.closest(
                '.faq-item'
              );

            if (!item) return;

            const answer =
              item.querySelector(
                '.faq-answer'
              );

            if (!answer) return;

            const isOpen =
              button.getAttribute(
                'aria-expanded'
              ) === 'true';

            button.setAttribute(
              'aria-expanded',
              String(!isOpen)
            );

            answer.hidden =
              isOpen;

            item.classList.toggle(
              'is-open',
              !isOpen
            );
          }
        );
      });
  });

  /* ==========================================
     DYNAMIC FAQPAGE JSON-LD SCHEMA
  ========================================== */

  /*
   * Add schema only when:
   *
   * 1. We actually found valid FAQs.
   * 2. We haven't already inserted ours.
   */
  if (
    faqSchemaItems.length > 0 &&
    !document.getElementById(
      'article-faq-jsonld'
    )
  ) {
    const faqSchema = {
      '@context':
        'https://schema.org',

      '@type': 'FAQPage',

      mainEntity:
        faqSchemaItems
    };

    const schemaScript =
      document.createElement('script');

    schemaScript.type =
      'application/ld+json';

    schemaScript.id =
      'article-faq-jsonld';

    /*
     * Serialize safely into script tag.
     */
    schemaScript.textContent =
      JSON.stringify(faqSchema)
        .replace(
          /</g,
          '\\u003c'
        );

    document.head.appendChild(
      schemaScript
    );
  }
});